import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {Subject, takeUntil} from 'rxjs';
import {OfferRuleAgeBucket, OfferRuleAgeBucketDiscountType} from '../../../../../model/offer-rule.model';

interface AgeBucketControls {
  maximum: FormControl<string>;
  discountType: FormControl<OfferRuleAgeBucketDiscountType>;
  discountValue: FormControl<string>;
}

interface AgeBucketView {
  readonly minimum: number | null;
  readonly bucket: OfferRuleAgeBucket;
  readonly rangeError: string;
  readonly valueError: string;
  readonly rangeLabel: string;
  readonly previewAmount: number | null;
  readonly capped: boolean;
}

const maximumAgeDays = 2147483647;
const maximumMoney = 99999999.99;
const maximumPercentage = 100;
const percentageScale = 100;
const centsPerDollar = 100;
const integerPattern = /^\d+$/;
const decimalPattern = /^\d+(?:\.\d{1,2})?$/;
const dollarCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

@Component({
  selector: 'auto-ipop-aged-inventory-discount-table',
  templateUrl: './ipop-aged-inventory-discount-table.component.html',
  styleUrls: ['./ipop-aged-inventory-discount-table.component.scss'],
  standalone: false
})
export class IpopAgedInventoryDiscountTableComponent implements OnChanges, OnInit, OnDestroy
{
  @Input() initialBuckets: readonly OfferRuleAgeBucket[] = [];
  @Input() maximumPrice: number | null = null;
  @Input() maxDiscountAmount: number | null = null;
  @Input() discountBasis: 'selling_price' | 'msrp' = 'selling_price';

  @Output() bucketsChange = new EventEmitter<OfferRuleAgeBucket[]>();
  @Output() readinessChange = new EventEmitter<boolean>();

  readonly bucketControls = new FormArray<FormGroup<AgeBucketControls>>([]);
  readonly bucketForm = new FormGroup({buckets: this.bucketControls});
  rows: AgeBucketView[] = [];
  addBucketGuidance = '';
  showDollarPreviews = false;
  basisLabel = 'Selling Price';
  capValid = true;

  private readonly destroySubject = new Subject<void>();

  /** Replaces rows only for a new input snapshot; price/basis changes only refresh derived state. */
  ngOnChanges(changes: SimpleChanges): void
  {
    if (changes.initialBuckets !== undefined) {
      this.bucketControls.clear({emitEvent: false});
      for (const bucket of this.initialBuckets) {
        this.bucketControls.push(this.buildBucketGroup(bucket), {emitEvent: false});
      }
    }
    this.refreshConfiguration();
  }

  /** Keeps invalid text editable without silently rounding or clamping a configured discount. */
  ngOnInit(): void
  {
    this.bucketControls.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(() =>
    {
      this.refreshConfiguration();
    });
  }

  /** Releases editor subscriptions when the dialog or rule type changes. */
  ngOnDestroy(): void
  {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  /** Appends a new final range only after the previous final maximum is finite and valid. */
  addBucket(): void
  {
    if (this.addBucketGuidance === '') {
      const bucket: OfferRuleAgeBucket = {
        minAgeDays: 0,
        maxAgeDays: null,
        discountType: 'percentage',
        discountValue: null
      };
      this.bucketControls.push(this.buildBucketGroup(bucket));
    }
  }

  /** Removing a row recalculates minima while preserving every remaining maximum and discount. */
  removeBucket(index: number): void
  {
    if (Number.isInteger(index) && index >= 0 && index < this.bucketControls.length) {
      this.bucketControls.removeAt(index);
    }
  }

  /** Text controls retain invalid precision and boundaries so users can correct them visibly. */
  private buildBucketGroup(bucket: OfferRuleAgeBucket): FormGroup<AgeBucketControls>
  {
    const group = new FormGroup<AgeBucketControls>({
      maximum: new FormControl(this.formatInput(bucket.maxAgeDays), {nonNullable: true}),
      discountType: new FormControl(bucket.discountType, {nonNullable: true}),
      discountValue: new FormControl(this.formatInput(bucket.discountValue), {nonNullable: true})
    });

    return group;
  }

  /** Null means blank; a configured zero must remain visible and editable. */
  private formatInput(value: number | null): string
  {
    let text = '';
    if (value !== null) {
      text = String(value);
    }

    return text;
  }

  /** Publishes independent drafts and a separate readiness result; invalid drafts cannot be staged. */
  private refreshConfiguration(): void
  {
    this.capValid = this.maxDiscountAmount === null
      || (this.isMoney(this.maxDiscountAmount) && this.maxDiscountAmount > 0);
    this.showDollarPreviews = this.isMoney(this.maximumPrice) && this.capValid;
    this.basisLabel = 'Selling Price';
    if (this.discountBasis === 'msrp') {
      this.basisLabel = 'MSRP';
    }
    this.rows = this.buildRows();
    this.addBucketGuidance = this.resolveAddBucketGuidance();
    const validBasis = this.discountBasis === 'selling_price' || this.discountBasis === 'msrp';
    const ready = this.rows.length > 0 && this.capValid && validBasis && this.isMoney(this.maximumPrice)
      && this.rows.every(row => row.rangeError === '' && row.valueError === '');
    this.bucketsChange.emit(this.rows.map(row => ({...row.bucket})));
    this.readinessChange.emit(ready);
  }

  /** Inclusive minima follow the immediately preceding maximum; invalid ranges remain in place. */
  private buildRows(): AgeBucketView[]
  {
    const rows: AgeBucketView[] = [];
    let minimum: number | null = 0;
    for (const group of this.bucketControls.controls) {
      const row = this.buildRow(group, minimum, rows.length);
      rows.push(row);
      minimum = null;
      if (this.isAgeInput(group.controls.maximum.value) && row.bucket.maxAgeDays !== null) {
        minimum = row.bucket.maxAgeDays + 1;
      }
    }

    return rows;
  }

  /** Invalid numeric text stays in the form and is represented by a non-saveable draft value. */
  private buildRow(group: FormGroup<AgeBucketControls>, minimum: number | null, index: number): AgeBucketView
  {
    const value = group.getRawValue();
    const bucket: OfferRuleAgeBucket = {
      minAgeDays: minimum ?? Number.NaN,
      maxAgeDays: this.parseInput(value.maximum),
      discountType: value.discountType,
      discountValue: this.parseInput(value.discountValue)
    };
    const rangeError = this.getRangeError(value.maximum, minimum, index);
    const valueError = this.getValueError(value.discountValue, value.discountType);
    const preview = this.buildPreview(bucket, rangeError === '' && valueError === '');
    const row: AgeBucketView = {
      minimum, bucket, rangeError, valueError,
      rangeLabel: this.getRangeLabel(bucket, rangeError),
      previewAmount: preview.amount,
      capped: preview.capped
    };

    return row;
  }

  /** Distinguishes an intentionally blank final maximum from malformed numeric text. */
  private parseInput(text: string): number | null
  {
    let value: number | null = null;
    if (text.trim() !== '') {
      value = Number(text);
    }

    return value;
  }

  /** Open-ended bounds are valid only on the final row; consumed ranges require correction. */
  private getRangeError(maximumText: string, minimum: number | null, index: number): string
  {
    let error = '';
    const maximum = this.parseInput(maximumText);
    if (maximumText.trim() !== '' && !this.isAgeInput(maximumText)) {
      error = `Enter a whole number from 0 to ${maximumAgeDays}.`;
    }
    if (maximum === null && index < this.bucketControls.length - 1) {
      error = 'Enter Maximum Days before the following bucket.';
    }
    if (minimum === null || minimum > maximumAgeDays) {
      error = 'Correct the preceding Maximum Days to establish this minimum.';
    }
    if (error === '' && maximum !== null && minimum !== null && maximum < minimum) {
      error = 'Maximum Days must be at least Minimum Days. Adjust this or the preceding range.';
    }

    return error;
  }

  /** Dollar values respect the shared cap; bucket percentages remain independent of scalar percentage flags. */
  private getValueError(text: string, type: OfferRuleAgeBucketDiscountType): string
  {
    const value = this.parseInput(text);
    let error = '';
    let maximum = maximumMoney;
    if (type === 'percentage') {
      maximum = maximumPercentage;
    }
    if (!decimalPattern.test(text.trim()) || value === null || !Number.isFinite(value) || value > maximum) {
      error = `Enter a value from 0 to ${maximum} with up to two decimal places.`;
    }
    if (type !== 'dollar' && type !== 'percentage') {
      error = 'Select Dollar or Percentage.';
    }
    if (error === '' && value !== null && type === 'dollar' && this.capValid && this.maxDiscountAmount !== null
      && value > this.maxDiscountAmount) {
      const discountAmount = dollarCurrencyFormatter.format(value);
      const maximumDiscountAmount = dollarCurrencyFormatter.format(this.maxDiscountAmount);
      error = `The ${discountAmount} discount exceeds the ${maximumDiscountAmount} maximum.`;
    }

    return error;
  }

  /** Finite integer storage bounds also prevent appending beyond the supported final day. */
  private isAgeInput(text: string): boolean
  {
    const value = Number(text);
    const valid = integerPattern.test(text.trim()) && Number.isInteger(value) && value <= maximumAgeDays;

    return valid;
  }

  /** Shared prices/caps must already satisfy the API's scale without rounding. */
  private isMoney(value: number | null): boolean
  {
    const valid = value !== null && Number.isFinite(value) && value >= 0 && value <= maximumMoney
      && decimalPattern.test(String(value));

    return valid;
  }

  /** Explains why appending would create a gap, overlap, or a range after an open-ended final row. */
  private resolveAddBucketGuidance(): string
  {
    let guidance = '';
    const last = this.rows[this.rows.length - 1];
    if (this.rows.some(row => row.rangeError !== '')) {
      guidance = 'Correct the day ranges before adding a bucket.';
    }
    if (last !== undefined && last.bucket.maxAgeDays === null) {
      guidance = 'Enter a finite value for Maximum Days in the final bucket before adding another.';
    }
    if (last !== undefined && last.bucket.maxAgeDays === maximumAgeDays) {
      guidance = 'The final bucket already reaches the maximum supported number of days.';
    }

    return guidance;
  }

  /** Invalid rows are never presented as a valid strategy interval. */
  private getRangeLabel(bucket: OfferRuleAgeBucket, error: string): string
  {
    let label = 'Range needs correction';
    if (error === '') {
      label = `${bucket.minAgeDays}+ days`;
      if (bucket.maxAgeDays !== null) {
        label = `${bucket.minAgeDays}–${bucket.maxAgeDays} days`;
      }
    }

    return label;
  }

  /** Q-01 uses Maximum Price for either basis; only this illustrative amount is capped and rounded. */
  private buildPreview(bucket: OfferRuleAgeBucket, valid: boolean): {amount: number | null; capped: boolean}
  {
    let amount: number | null = null;
    let capped = false;
    if (valid && this.showDollarPreviews && bucket.discountValue !== null) {
      let discountCents = this.getIllustrativeDiscountCents(bucket);
      const capCents = Math.round(this.maxDiscountAmount * centsPerDollar);
      if (this.maxDiscountAmount !== null && discountCents > capCents) {
        discountCents = capCents;
        capped = true;
      }
      amount = Math.round(discountCents) / centsPerDollar;
    }

    return {amount, capped};
  }

  /** Integer inputs preserve half-cent rounding; allowed products remain within JavaScript's safe integer range. */
  private getIllustrativeDiscountCents(bucket: OfferRuleAgeBucket): number
  {
    let discountCents = Math.round(bucket.discountValue * centsPerDollar);
    if (bucket.discountType === 'percentage') {
      const priceCents = Math.round(this.maximumPrice * centsPerDollar);
      const percentageHundredths = Math.round(bucket.discountValue * percentageScale);
      discountCents = priceCents * percentageHundredths / (maximumPercentage * percentageScale);
    }

    return discountCents;
  }
}
